import type { AuthenticatedPlatformTransport } from './authenticatedPlatformTransport';

/** 平台当前可购买套餐。积分与价格分开，禁止互相折算。 */
export interface SubscriptionPlan {
  id: number; title: string; subtitle: string; price_amount: number; currency: string;
  duration_unit: string; duration_value: number; credit_base: number | null; credit_bonus: number | null;
}
/** 支付结果只由平台订单状态决定，浏览器计时结束不等于支付失败。 */
export interface SubscriptionOrder {
  trade_no: string; qr_code: string; amount: number | null; amount_minor: number | null; expires_at: number;
  status: string;
  payment_method?: string; pay_url?: string;
}
/** 仅携带可展示的业务拒绝原因，网络与服务端异常使用界面通用提示。 */
export class SubscriptionRequestError extends Error {
  /** message 为平台业务提示；界面按纯文本渲染，不解释 HTML。 */
  constructor(message: string) { super(message); }
}
/** 下单已成功、后续校验失败时携带可恢复订单，禁止丢号后重复购买。 */
export class SubscriptionOrderRecoveryError extends Error {
  readonly recovery: SubscriptionOrder;
  /** 金额未知时不展示二维码；仅允许查询既有订单恢复权威数据。 */
  constructor(tradeNo: string, method: string, cause: unknown) {
    super(cause instanceof Error ? cause.message : 'Order verification failed');
    this.recovery = { trade_no: tradeNo, payment_method: method, status: 'unknown',
      amount: null, amount_minor: null, expires_at: 0, qr_code: '' };
  }
}
/** 个人订单列表只保留业务字段，不接收管理员/支付渠道原始数据。 */
export interface PersonalOrder {
  trade_no: string; plan_title: string; payment_method: string; status: string;
  money: number; create_time: number; order_type: string;
}
/** 套餐附属加购配置，必须具有生效主订阅。 */
export interface SubscriptionAddon {
  plan_id: number; title: string; credits: number; price_minor: number; valid_days: number;
}
/** 检查数值，金额缺失不能按零元购买。 */
function nonnegative(value: unknown): number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) throw new Error('Invalid payment number');
  return value;
}
/** 读取平台订单，不接受页面 URL 中的支付成功声明。 */
function parseOrder(value: unknown): SubscriptionOrder {
  const d = value as Record<string, unknown>;
  if (!d || typeof d.trade_no !== 'string' || !d.trade_no || typeof d.status !== 'string') throw new Error('Invalid order');
  const amount = nonnegative(d.amount), minor = nonnegative(d.amount_minor);
  if (!Number.isSafeInteger(minor) || Math.round(amount * 100) !== minor) throw new Error('Invalid payment amount');
  return { trade_no: d.trade_no, qr_code: typeof d.qr_code === 'string' ? d.qr_code : '', amount,
    amount_minor: minor, expires_at: nonnegative(d.expires_at), status: d.status };
}
/** 套餐查询与银联商务订单，沿用桌面端同一平台契约，不增加扣款或履约逻辑。 */
export function createSubscriptionClient(transport: AuthenticatedPlatformTransport) {
  /** 限定当前登录周期；失败抛出稳定错误码供 UI 国际化显示。 */
  async function request(path: string, body?: Record<string, unknown>): Promise<unknown> {
    const identity = transport.identity();
    if (!identity) throw new Error('unauthenticated');
    const isCurrent = () => identity === transport.identity();
    const result = await transport.request(path, isCurrent, body);
    if (!isCurrent()) throw new Error('superseded');
    if (!result.success || result.status < 200 || result.status >= 300) {
      if (result.status === 200 && typeof result.message === 'string' && result.message.trim()) {
        throw new SubscriptionRequestError(result.message.trim().slice(0, 300));
      }
      throw new Error(`subscription_${result.status}`);
    }
    return result.data;
  }
  /** 订单历史与单笔查询复用字段校验，金额缺失时拒绝显示。 */
  function personalOrder(value: unknown): PersonalOrder {
    const d = value as Record<string, unknown>;
    if (!d || typeof d.trade_no !== 'string' || !d.trade_no || typeof d.status !== 'string'
      || typeof d.payment_method !== 'string') throw new Error('Invalid personal order');
    return { trade_no: d.trade_no, status: d.status, payment_method: d.payment_method,
      plan_title: String(d.plan_title ?? ''), order_type: String(d.order_type ?? ''),
      money: nonnegative(d.money), create_time: nonnegative(d.create_time) };
  }
  return {
    /** 按平台用户权限分页查询订单；与管理端接口完全分离。 */
    async history(page = 1): Promise<{ items: PersonalOrder[]; total: number }> {
      if (!Number.isSafeInteger(page) || page < 1) throw new Error('Invalid page');
      const data = await request(`/api/subscription/orders?p=${page}&page_size=10`) as Record<string, unknown>;
      if (!data || !Array.isArray(data.items) || !Number.isSafeInteger(data.total) || Number(data.total) < 0) throw new Error('Invalid order list');
      return { items: data.items.map(personalOrder), total: Number(data.total) };
    },
    /** 查询加购资格与已经配置的渠道，任何一项失败都不能推定有资格。 */
    async addons(): Promise<{ plans: SubscriptionAddon[]; methods: ('wechat' | 'alipay')[] }> {
      const [self, plans, info] = await Promise.all([
        request('/api/subscription/self'), request('/api/subscription/plans'), request('/api/user/topup/info'),
      ]) as [Record<string, unknown>, { plan: Record<string, unknown> }[], Record<string, unknown>];
      if (!self || !Array.isArray(self.subscriptions) || !Array.isArray(plans) || !info) throw new Error('Invalid addon configuration');
      const active = new Set(self.subscriptions.map(row => row.subscription?.plan_id).filter(id => Number.isSafeInteger(id)));
      return { methods: [...(info.enable_wechat_pay === true ? ['wechat' as const] : []), ...(info.enable_alipay === true ? ['alipay' as const] : [])],
        plans: plans.map(row => row.plan).filter(p => p?.enabled === true && p.currency === 'CNY' && active.has(p.id)
          && Number.isSafeInteger(p.addon_pack_credits) && Number(p.addon_pack_credits) > 0
          && Number.isSafeInteger(p.addon_pack_price_minor) && Number(p.addon_pack_price_minor) > 0)
          .map(p => ({ plan_id: Number(p.id), title: String(p.title ?? ''), credits: Number(p.addon_pack_credits),
            price_minor: Number(p.addon_pack_price_minor), valid_days: nonnegative(p.addon_pack_valid_days) })) };
    },
    /** 用户明确选择加购渠道后下单；金额和状态随后从个人订单读取。 */
    async createAddon(planId: number, method: 'wechat' | 'alipay'): Promise<SubscriptionOrder> {
      if (!Number.isSafeInteger(planId) || planId <= 0 || !['wechat', 'alipay'].includes(method)) throw new Error('Invalid addon');
      const identity = transport.identity();
      const data = await request(`/api/subscription/addon/${method}/pay`, { plan_id: planId, ...(method === 'wechat' ? { is_mobile: false } : {}) }) as Record<string, unknown>;
      if (!data || typeof data.trade_no !== 'string' || !data.trade_no) throw new Error('Invalid addon order');
      try {
      let payUrl = '';
      if (method === 'alipay') {
        const url = new URL(String(data.pay_url));
        if (url.protocol !== 'https:' || url.username || url.password) throw new Error('Invalid payment URL');
        payUrl = url.href;
      }
      const details = await request(`/api/subscription/orders/${encodeURIComponent(data.trade_no)}`) as Record<string, unknown>;
      const order = personalOrder(details?.order);
      if (identity !== transport.identity()) throw new Error('superseded');
      if (order.trade_no !== data.trade_no || order.payment_method !== `${method}_native`) throw new Error('Order mismatch');
      return { trade_no: order.trade_no, amount: order.money, amount_minor: Math.round(order.money * 100),
        status: order.status, expires_at: 0, payment_method: `${method}_native`, pay_url: payUrl,
        qr_code: method === 'wechat' && typeof data.code_url === 'string' ? data.code_url : '' };
      } catch (error) {
        if (identity !== transport.identity()) throw new Error('superseded');
        throw new SubscriptionOrderRecoveryError(data.trade_no, `${method}_native`, error);
      }
    },
    /** 非银联订单从当前用户订单接口获取回调后的状态，不信任浏览器返回参数。 */
    async personalOrder(tradeNo: string): Promise<SubscriptionOrder> {
      if (!tradeNo || tradeNo.length > 128) throw new Error('Invalid trade number');
      const data = await request(`/api/subscription/orders/${encodeURIComponent(tradeNo)}`) as Record<string, unknown>;
      const order = personalOrder(data?.order);
      if (order.trade_no !== tradeNo) throw new Error('Order mismatch');
      return { trade_no: order.trade_no, amount: order.money, amount_minor: Math.round(order.money * 100), status: order.status,
        expires_at: 0, payment_method: order.payment_method, qr_code: '' };
    },
    /** 获取启用套餐，价格按平台币种显示，未知积分不猜测。 */
    async plans(): Promise<SubscriptionPlan[]> {
      const data = await request('/api/subscription/plans');
      if (!Array.isArray(data)) throw new Error('Invalid plans');
      return data.map(row => row.plan).filter(p => p?.enabled === true).map(p => {
        if (!Number.isSafeInteger(p.id) || p.id <= 0 || typeof p.title !== 'string' || typeof p.currency !== 'string') throw new Error('Invalid plan');
        return { id: p.id, title: p.title, subtitle: String(p.subtitle ?? ''), price_amount: nonnegative(p.price_amount),
          currency: p.currency, duration_unit: String(p.duration_unit ?? ''), duration_value: nonnegative(p.duration_value),
          credit_base: p.credit_base == null ? null : nonnegative(p.credit_base), credit_bonus: p.credit_bonus == null ? null : nonnegative(p.credit_bonus) };
      });
    },
    /** 用户明确点击购买后创建订单；此方法不自动重试 POST。 */
    async create(planId: number): Promise<SubscriptionOrder> {
      if (!Number.isSafeInteger(planId) || planId <= 0) throw new Error('Invalid plan ID');
      const data = await request('/api/subscription/chinaums/pay', { plan_id: planId });
      try { return parseOrder(data); }
      catch (error) {
        const tradeNo = (data as Record<string, unknown> | null)?.trade_no;
        // 平台已经受理的订单必须保留，不能把校验失败当成未下单而让用户重复购买。
        if (typeof tradeNo === 'string' && tradeNo && tradeNo.length <= 128) {
          throw new SubscriptionOrderRecoveryError(tradeNo, 'chinaums', error);
        }
        throw error;
      }
    },
    /** 读取自己订单的权威状态，refresh 请求平台核对支付渠道。 */
    async order(tradeNo: string, refresh = false): Promise<SubscriptionOrder> {
      if (!tradeNo || tradeNo.length > 128) throw new Error('Invalid trade number');
      const order = parseOrder(await request(`/api/subscription/chinaums/orders/${encodeURIComponent(tradeNo)}${refresh ? '?refresh=true' : ''}`));
      if (order.trade_no !== tradeNo) throw new Error('Order mismatch');
      return order;
    },
  };
}
